import {
  useState,
  useEffect,
  useMemo,
  useRef,
  RefObject,
  InputHTMLAttributes,
} from "react";

import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import { RankingInfo, rankItem } from "@tanstack/match-sorter-utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Column,
  ColumnDef,
  ColumnFiltersState,
  FilterFn,
  OnChangeFn,
  Row,
  SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";

import {
  AppointmentStatus,
  AppointmentStatusSlug,
  getAppointmentStatusValues,
} from "@/_constants/appointment";
import {
  getServiceOrderManagedByValuesByBoolean,
  businessServiceTypeValues,
  serviceOrderManagedByValues,
  ServiceOrderType,
  serviceOrderTypeValues,
} from "@/_constants/serviceOrder";
import { tenantIcon, tenantTitle } from "@/_constants/tenantType.ts";
import {
  displayMinName,
  getDateFromIso,
  getFullNameWithFirstnameInitial,
  getFullName,
} from "@/_helpers/stringHelper.ts";
import { useGenerateCustomerInvoice } from "@/_services/hooks/invoice.hooks.ts";
import {
  convertToStartDate,
  parseDateFromString,
} from "@/_utils/date.utils.ts";
import AgencyLinkComponent from "@/common/components/AgencyLinkComponent.tsx";
import BusinessServiceTypeMinComponent from "@/common/components/BusinessServiceTypeMinComponent";
import ColoredMarker from "@/common/components/ColoredMarker.tsx";
import ErpExpressQuoteBanner from "@/common/components/ErpExpressQuoteBanner/ErpExpressQuoteBanner";
import GridActionButtons from "@/common/components/GridActionButtons.tsx";
import Loader from "@/common/components/Loader.tsx";
import { ModalInfo } from "@/common/components/ModalInfo/ModalInfo.tsx";
import ServiceManagedByComponent from "@/common/components/ServiceManagedByComponent";
import ServiceStatusComponent from "@/common/components/ServiceStatusComponent";
import ServiceTypeMinComponent from "@/common/components/ServiceTypeMinComponent";
import { AscendingIcon } from "@/common/components/svg-icons/AscendingIcon.tsx";
import { DescendingIcon } from "@/common/components/svg-icons/DescendingIcon.tsx";
import { DoubleLeftArrowIcon } from "@/common/components/svg-icons/DoubleLeftArrowIcon.tsx";
import { LeftArrowIcon } from "@/common/components/svg-icons/LeftArrowIcon.tsx";
import { UnsortedIcon } from "@/common/components/svg-icons/UnsortedIcon.tsx";
import { TableFilterTitle } from "@/common/components/table/filter-title/TableFilterTitle.tsx";
import { GridType, Permissions } from "@/common/enums/sharedEnums.ts";
import { IColumnFilter, ITableParams } from "@/common/interfaces";
import { useGetUserType } from "@/modules/auth/hooks/userType.hooks.ts";
import { DWELLING_DETAILS } from "@/modules/dwelling/routes/index.ts";
import {
  CustomerInvoiceError,
  InvoiceErrorEnum,
} from "@/modules/order/interfaces/customerInvoice.ts";
import { getOrderViewUrl } from "@/modules/order/routes";
import { exportServices } from "@/modules/order/services";
import { CustomerInvoice } from "@/modules/profile/interfaces/customer.ts";
import { IPerson, IService } from "@/modules/serviceOrdering/reducers";

import history from "../../../routes/history.ts";
import { IResponsePaginateServices } from "../reducers";
import { cancelService, getServices } from "../services";

declare module "@tanstack/react-table" {
  interface FilterFns {
    fuzzy: FilterFn<unknown>;
  }
  interface FilterMeta {
    itemRank: RankingInfo;
  }
}

const DEFAULT_PER_PAGE = 10;
// const FILTER_PER_PAGE = 200;

// Improved in-memory cache with pre-warmed values support
const rankCache = new Map<string, any>();

// eslint-disable-next-line react-refresh/only-export-components
export const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
  const cellValue = row.getValue(columnId) as unknown as string;
  const cacheKey = `${cellValue}::${value}`;

  // Return cached result if available
  if (rankCache.has(cacheKey)) {
    const cachedRank = rankCache.get(cacheKey);
    addMeta({ itemRank: cachedRank });
    return cachedRank.passed;
  }

  // Optimize by caching intermediate results for common search values
  if (!rankCache.has(cellValue)) {
    rankCache.set(cellValue, new Map<string, any>());
  }
  const cellCache = rankCache.get(cellValue);

  // If we've already ranked this cellValue for other values, check those first
  if (cellCache.has(value)) {
    const cachedRank = cellCache.get(value);
    addMeta({ itemRank: cachedRank });
    return cachedRank.passed;
  }

  // Rank the item
  const itemRank = rankItem(cellValue, value);

  // Store the result in both general and specific caches
  addMeta({ itemRank });
  cellCache.set(value, itemRank);
  rankCache.set(cacheKey, itemRank);

  return itemRank.passed;
};

const MyOrdersContainer = () => {
  const queryClient = useQueryClient();

  const { isSuperAdmin } = useGetUserType();

  // Always define data, even if it's loading or errored
  // const data = servicesData?.services ?? [];

  // const rerender = useReducer(() => ({}), {})[1];

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState([]);

  const createdAtRef = useRef<Calendar>(null);
  const leaseStartDateRef = useRef<Calendar>(null);
  const leaseEndDateRef = useRef<Calendar>(null);
  const appointmentStartDateRef = useRef<Calendar>(null);

  /**
   * === CUSTOMER INVOICE ===
   */
  const [showSuccessInvoiceModal, setShowSuccessInvoiceModal] = useState(false);
  const [showErrorInvoiceModal, setShowErrorInvoiceModal] = useState(false);
  const [errorInvoiceMessage, setErrorInvoiceMessage] = useState("");

  const [showQuoteBanner, setShowQuoteBanner] = useState(false);
  const [erpOrderId, setErpOrderId] = useState<string>("");

  useEffect(() => {
    const shouldShowBanner = localStorage.getItem("showErpExpressQuoteBanner");
    const orderId = localStorage.getItem("erpExpressOrderId");

    if (shouldShowBanner === "true") {
      setShowQuoteBanner(true);
      setErpOrderId(orderId || "");
      localStorage.removeItem("showErpExpressQuoteBanner");
      localStorage.removeItem("erpExpressOrderId");
    }
  }, []);

  const getInvoiceCustomer = (service: IService | undefined) => {
    let invoiceCustomer;

    // Check if service type is ERP, and if agency does NOT manage ERP billings
    if (
      service?.type === ServiceOrderType.ERP_EXPRESS &&
      service?.dwelling?.agency?.manageErpBillings === false
    ) {
      invoiceCustomer = service?.dwelling?.companyLessor?.customer;
    } else {
      invoiceCustomer = service?.dwelling?.agency?.customer;
    }

    return invoiceCustomer;
  };

  const getInvoiceErrorMessage = (errorMessage: string) => {
    switch (errorMessage) {
      case InvoiceErrorEnum.CUSTOMER_NOT_FOUND:
        return "L'agence liée à cette commande n'a pas de client";
      case InvoiceErrorEnum.INVOICE_CONFIG_NOT_FOUND:
        return "Veuillez sélectionner le type d'offre et la fréquence de facturation pour l'agence liée à cette commande";
      case InvoiceErrorEnum.PRODUCT_NOT_FOUND:
        return "Le produit lié à cette commande n'existe pas";
      case InvoiceErrorEnum.INVOICE_NOT_FOUND:
        return "La facture liée à cette commande n'existe pas";
      default:
        return errorMessage;
    }
  };

  const { mutate: mutateGenerateInvoice, isLoading: isLoadingGenerateInvoice } =
    useGenerateCustomerInvoice({
      handleSuccess: () => {
        setShowSuccessInvoiceModal(true);
      },
      handleError: (error) => {
        const errorMessage = (error?.response?.data as CustomerInvoiceError)
          ?.message;

        if (errorMessage) {
          const message = getInvoiceErrorMessage(errorMessage);
          setErrorInvoiceMessage(message);
          setShowErrorInvoiceModal(true);
        }
      },
    });

  const handleGenerateInvoice = (row: Row<IService>) => {
    mutateGenerateInvoice(row?.original?.report?.id || "");
  };
  /**
   * === END OF CUSTOMER INVOICE ===
   */

  const [tableParams, setTableParams] = useState<ITableParams>({
    perPage: DEFAULT_PER_PAGE,
    currentPage: 1,
    filter: "",
    filters: [],
  });

  useEffect(() => {
    setTableParams((prev) => ({
      ...prev,
      currentPage: 1,
      filters: columnFilters as IColumnFilter[],
      // perPage: columnFilters?.length > 0 ? FILTER_PER_PAGE : DEFAULT_PER_PAGE,
    }));
  }, [columnFilters]);

  useEffect(() => {
    setTableParams((prev) => ({
      ...prev,
      currentPage: 1,
      sorting,
    }));
  }, [sorting]);

  const {
    data: servicesData,
    isLoading,
    isError,
    error,
    isFetching,
  } = useQuery<IResponsePaginateServices, Error>(
    ["orders", tableParams],
    () => getServices(tableParams),
    {
      keepPreviousData: true,
    }
  );

  const handleGlobalFilter = (value: string | number) => {
    setGlobalFilter(String(value));
    setTableParams((prev) => ({
      ...prev,
      currentPage: 1,
      filter: String(value),
      // perPage: value ? FILTER_PER_PAGE : DEFAULT_PER_PAGE,
    }));
  };

  const manageColumnFilters = (id: string, value: string | undefined) => {
    if (value) {
      setColumnFilters((prev) => [
        ...prev.filter((filter) => filter.id !== id),
        {
          id: id,
          value,
        },
      ]);
    } else {
      setColumnFilters((prev) => prev.filter((filter) => filter.id !== id));
    }
  };

  const onFilterByBadgeSlug = (slug: AppointmentStatusSlug | undefined) => {
    const slugName = slug ? getAppointmentStatusValues(slug).name : undefined;
    manageColumnFilters("slug", slugName);
  };

  const clearFilterHandler = () => {
    handleGlobalFilter("");
    setColumnFilters([]);
  };

  const getStatusColumnFilter = () => {
    const slugName = tableParams.filters?.find(
      (filter) => filter.id === "slug"
    )?.value;

    const selectValue = slugName
      ? getAppointmentStatusValues(slugName, true).slug
      : undefined;

    return (
      <Dropdown
        placeholder="Statut"
        value={selectValue}
        options={Object.entries(AppointmentStatus).map(([slug, value]) => ({
          label: value,
          value: slug,
        }))}
        onChange={(e) => onFilterByBadgeSlug(e.target.value)}
        showClear
        className="p-column-filter column-custom-filter"
        panelClassName="column-select-filter-panel"
        itemTemplate={(option) => (
          <ServiceStatusComponent
            slug={option.value as AppointmentStatusSlug}
          />
        )}
      />
    );
  };

  const getManagedByColumnFilter = () => {
    return (
      <Dropdown
        placeholder="Gestionnaire"
        value={
          tableParams.filters?.find((filter) => filter.id === "managedBy")
            ?.value
        }
        options={Object.values(serviceOrderManagedByValues).map((el) => ({
          label: el.label,
          value: el.label,
        }))}
        onChange={(e) => manageColumnFilters("managedBy", e.target.value)}
        showClear
        className="p-column-filter column-custom-filter"
        panelClassName="column-select-filter-panel"
        itemTemplate={(option) => (
          <ServiceManagedByComponent label={option.label} />
        )}
      />
    );
  };

  const getTypeColumnFilter = () => {
    return (
      <Dropdown
        placeholder="Prestation"
        value={
          tableParams.filters?.find((filter) => filter.id === "type")?.value
        }
        options={Object.values(serviceOrderTypeValues).map((el) => ({
          label: el.label,
          value: el.name,
        }))}
        onChange={(e) => manageColumnFilters("type", e.target.value)}
        showClear
        className="p-column-filter column-custom-filter"
        panelClassName="column-select-filter-panel"
        itemTemplate={(option) => (
          <ServiceTypeMinComponent label={option.label} />
        )}
      />
    );
  };

  const getBusinessTypeColumnFilter = () => {
    return (
      <Dropdown
        placeholder="Métier"
        value={
          tableParams.filters?.find(
            (filter) => filter.id === "businessServiceType"
          )?.value
        }
        options={Object.values(businessServiceTypeValues).map((el) => ({
          label: el.labelMin,
          value: el.name,
        }))}
        onChange={(e) =>
          manageColumnFilters("businessServiceType", e.target.value)
        }
        showClear
        className="p-column-filter column-custom-filter"
        panelClassName="column-select-filter-panel"
        itemTemplate={(option) => (
          <BusinessServiceTypeMinComponent label={option.label} />
        )}
      />
    );
  };

  const getDateColumnFilter = (id: string, ref: RefObject<Calendar>) => {
    // Retrieve the current filter value.
    const filterValue = tableParams.filters?.find(
      (filter) => filter.id === id
    )?.value;

    // Convert the string value to a Date if available.
    let date: Date | null = null;
    if (filterValue) {
      const dateParts = filterValue.split("/");
      if (dateParts.length === 3) {
        date = parseDateFromString(filterValue);
      } else {
        date = new Date(filterValue);
      }
    }
    const isValidDate = date instanceof Date && !isNaN(date.getTime());

    return (
      <div onClick={() => ref.current?.show()}>
        <Calendar
          ref={ref}
          value={isValidDate ? date : null}
          onChange={(e) =>
            manageColumnFilters(
              id,
              e.value ? convertToStartDate(e.value) : undefined
            )
          }
          className="column-custom-filter"
          dateFormat="dd/mm/yy"
          placeholder="jj/mm/aaaa"
        />
      </div>
    );
  };

  const removeService = useMutation({
    mutationFn: (idService: string) => cancelService(idService),
    onError: (error) => {
      console.error(error);
      toast.error("Erreur lors de l'annulation de la commande");
    },
    onSuccess: async () => {
      toast.success("La commande a été annulé avec succès");
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
  });

  const handleExport = async () => {
    if (!servicesData?.hasSuperAdminPermission) {
      return;
    }
    try {
      const response = await exportServices();
      location.href = response.exportFileUrl;
    } catch (e) {
      toast.error(
        "Impossible de généré votre export CSV.\nVeuillez contacter votre administrateur système."
      );
    }
  };

  // Define columns based on the IService interface
  const columns = useMemo<ColumnDef<IService, any>[]>(
    () => [
      {
        accessorFn: (row) => row?.appointment?.lastStatus?.status.status,
        id: "slug",
        header: () => "Status",
        cell: ({ row }) => (
          <div
            onClick={() => history.push(getOrderViewUrl(row.original.id || ""))}
          >
            <ServiceStatusComponent
              slug={row?.original?.appointment?.lastStatus?.status.slug}
            />
          </div>
        ),
        filterFn: "includesString",
        sortingFn: "alphanumeric",
      },
      {
        accessorFn: (row) => row?.datedReference,
        id: "datedReference",
        header: () => "Ref",
        cell: ({ row }) =>
          row.original.permissions.includes(Permissions.SERVICE_GET) ? (
            <Link
              to={getOrderViewUrl(row.original.id || "")}
              className="propertyAdressLink link-td text-center d-flex flex-row justify-content-center gap-2"
            >
              {row.original.datedReference}
            </Link>
          ) : (
            <span className="propertyAdressLink link-td text-center d-flex flex-row justify-content-center gap-2">
              {row.original.datedReference}
            </span>
          ),
        filterFn: "includesString",
        sortingFn: "datetime",
      },
      {
        accessorFn: (row) => getDateFromIso(row.createdAt),
        id: "createdAt",
        header: () => "Date de commande",
        cell: ({ row }) => getDateFromIso(row.original.createdAt),
        filterFn: "includesString",
        sortingFn: "datetime",
      },
      {
        accessorFn: (row) =>
          row?.dwelling?.agency?.label?.toLowerCase()?.trim(),
        id: "agency",
        header: () => "Agence",
        cell: ({ row }) => (
          <AgencyLinkComponent
            agencyId={row.original.dwelling.agency.id}
            agencyLabel={row.original.dwelling.agency.label}
          />
        ),
        filterFn: "includesString",
        sortingFn: "alphanumeric",
      },
      {
        accessorFn: (row) =>
          getServiceOrderManagedByValuesByBoolean(row.isManagedByMainGroup)
            ?.label,
        id: "managedBy",
        header: () => "Gestionnaire",
        cell: ({ row }) => (
          <ServiceManagedByComponent
            isManagedByMainGroup={row.original.isManagedByMainGroup}
          />
        ),
        filterFn: "includesString",
        sortingFn: "alphanumeric",
      },
      {
        accessorFn: (row) => row.businessServiceType,
        id: "businessServiceType",
        header: () => "Métier",
        cell: ({ row }) => (
          <BusinessServiceTypeMinComponent
            businessType={row.original.businessServiceType}
          />
        ),
        filterFn: "includesString",
        sortingFn: "alphanumeric",
      },
      {
        accessorFn: (row) => row.type,
        id: "type",
        header: () => "Prestation",
        cell: ({ row }) => (
          <ServiceTypeMinComponent
            type={row.original.type as ServiceOrderType}
          />
        ),
        filterFn: "includesString",
        sortingFn: "alphanumeric",
      },
      {
        accessorFn: (row) => {
          const people = row.appointment?.people || [];
          const fullName = [
            getFullNameWithFirstnameInitial({
              firstname: people[0]?.firstname,
              lastname: people[0]?.lastname,
            }),
            getFullNameWithFirstnameInitial({
              firstname: people[1]?.firstname,
              lastname: people[1]?.lastname,
            }),
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();

          return fullName;
        },
        id: "tenant",
        header: () => "Occupant(s)",
        cell: ({ row }) => {
          return row.original.appointment?.people?.map(
            (person: IPerson, index: number) => {
              return (
                <div
                  key={index}
                  className="d-flex gap-2"
                  data-toggle="tooltip"
                  data-placement="top"
                  title={tenantTitle(person?.lastTenant?.type)}
                >
                  <div className="fw-bold">
                    {displayMinName(person.lastname, person.firstname)}
                  </div>
                  <i className={tenantIcon(person?.lastTenant?.type)}></i>
                </div>
              );
            }
          );
        },
        filterFn: "includesString",
        sortingFn: "alphanumeric",
      },
      {
        accessorFn: (row) => {
          if (
            [
              ServiceOrderType.INCOMING_INVENTORY,
              ServiceOrderType.OUTGOING_INCOMING_INVENTORY,
            ].includes(row.type as keyof object)
          ) {
            const leaseStartDate =
              row.appointment?.people[0]?.lastTenant?.leaseStartDate ?? null;
            return leaseStartDate ? getDateFromIso(leaseStartDate) : "-";
          }
          return "-";
        },
        id: "leaseStartDate",
        header: () => "Signature de Bail",
        cell: ({ row }) => {
          if (
            [
              ServiceOrderType.INCOMING_INVENTORY,
              ServiceOrderType.OUTGOING_INCOMING_INVENTORY,
            ].includes(row.original.type as keyof object)
          ) {
            const leaseStartDate =
              row.original.appointment?.people[0]?.lastTenant?.leaseStartDate ??
              null;
            return leaseStartDate ? getDateFromIso(leaseStartDate) : "-";
          }
          return "-";
        },
        filterFn: "includesString",
        sortingFn: "datetime",
      },
      {
        accessorFn: (row) => {
          if (
            [
              ServiceOrderType.OUTGOING_INVENTORY,
              ServiceOrderType.OUTGOING_INCOMING_INVENTORY,
            ].includes(row.type as keyof object) &&
            row.appointment.people[0]
          ) {
            const leastEndDate =
              row.appointment.people[0]?.lastTenant?.leaseEndDate ?? null;
            return leastEndDate ? getDateFromIso(leastEndDate) : "-";
          }
          return "-";
        },
        id: "leaseEndDate",
        header: () => "Fin de Bail",
        cell: ({ row }) => {
          if (
            [
              ServiceOrderType.OUTGOING_INVENTORY,
              ServiceOrderType.OUTGOING_INCOMING_INVENTORY,
            ].includes(row.original.type as keyof object) &&
            row.original.appointment.people[0]
          ) {
            const leastEndDate =
              row.original.appointment.people[0]?.lastTenant?.leaseEndDate ??
              null;
            return leastEndDate ? getDateFromIso(leastEndDate) : "-";
          }
          return "-";
        },
        filterFn: "includesString",
        sortingFn: "datetime",
      },
      {
        accessorFn: (row) => getDateFromIso(row.appointment?.startDate),
        id: "appointmentStartDate",
        header: () => "Date RDV",
        cell: ({ row }) => getDateFromIso(row.original.appointment?.startDate),
        filterFn: "includesString",
        sortingFn: "datetime",
      },
      {
        accessorFn: (row) =>
          row.dwelling.category.value === "BUILD"
            ? row.dwelling.batchNumber
            : row.dwelling.managementLotNumber,
        id: "batchNumber",
        header: () => "Lot(s)",
        cell: ({ row }) =>
          row.original.dwelling.category.value === "BUILD" ? (
            <span>
              {row.original.dwelling.batchNumber
                ? `🏢 ${row.original.dwelling.batchNumber} Lots`
                : "-"}
            </span>
          ) : (
            <span>
              {row.original.dwelling.managementLotNumber
                ? `🏠 Lot ${row.original.dwelling.managementLotNumber}`
                : "-"}
            </span>
          ),
        filterFn: "includesString",
        sortingFn: "alphanumeric",
      },
      {
        accessorFn: (row) =>
          `${row?.dwelling?.address?.address?.address?.toLowerCase()?.trim()} ${row?.dwelling?.address?.address.zip?.toLowerCase()?.trim()} ${row?.dwelling?.address?.address?.city?.toLowerCase()?.trim()}`,
        id: "address",
        header: () => "Adresse",
        cell: ({ row }) => (
          <Link
            to={DWELLING_DETAILS.replace(":id", row.original.dwelling.id)}
            className="propertyAdressLink  link-td text-center d-flex flex-row justify-content-center gap-2"
          >
            {row.original.dwelling.address.address.address}{" "}
            {row.original.dwelling.address.address.zip}{" "}
            {row.original.dwelling.address.address.city}
          </Link>
        ),
        filterFn: "includesString",
        sortingFn: "alphanumeric",
      },
      {
        accessorFn: (row) =>
          getFullName({
            firstname: row?.dwelling?.lessorFirstname,
            lastname: row?.dwelling?.lessorName,
            startsWithLastname: true,
          })?.toLowerCase(),
        id: "lessor",
        header: () => "Bailleur",
        cell: ({ row }) =>
          getFullName({
            firstname: row?.original?.dwelling?.lessorFirstname,
            lastname: row?.original?.dwelling?.lessorName,
            startsWithLastname: true,
          }),
        filterFn: "includesString",
        sortingFn: "alphanumeric",
      },
      {
        id: "actions",
        header: () => "Actions",
        cell: ({ row }) => (
          <div>
            <GridActionButtons
              {...(row.original.permissions.includes(
                Permissions.SERVICE_GET
              ) && {
                viewUrl: getOrderViewUrl(row.original.id || ""),
                downloadableUrl: row.original?.mainFile?.url,
              })}
              disabledInvoiceButton={getInvoiceCustomer(row?.original)
                ?.invoices?.filter(
                  (item: CustomerInvoice) => item?.service_report_id
                )
                ?.map((item: CustomerInvoice) => item.service_report_id)
                ?.includes(`${row?.original?.report?.id}`)}
              {...(isSuperAdmin &&
                row.original.permissions.includes(Permissions.SERVICE_GET) &&
                row.original.appointment.lastStatus.status.slug ===
                  AppointmentStatusSlug.DONE &&
                row.original.isManagedByMainGroup && {
                  onCreateInvoice: () => handleGenerateInvoice(row),
                })}
              {...(row.original.permissions.includes(
                Permissions.SERVICE_UPDATE
              ) && {
                remove: () => removeService.mutate(row.original.id),
              })}
              type={GridType.service}
            />
          </div>
        ),
        meta: {
          frozen: true,
        },
      },
    ],
    []
  );

  const table = useReactTable({
    data: servicesData?.services ?? [],
    columns,
    filterFns: {
      fuzzy: fuzzyFilter, // Define as a filter function that can be used in column definitions
    },
    state: {
      columnFilters,
      globalFilter,
      sorting,
      pagination: {
        pageIndex: 0,
        pageSize: tableParams.perPage || 10,
      },
    },
    pageCount: servicesData?.lastPage,
    manualSorting: true,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: "fuzzy", // Apply fuzzy filter to the global filter
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(), // Client-side filtering
    onSortingChange: setSorting as OnChangeFn<SortingState> | undefined,
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    debugTable: false,
    debugHeaders: false,
    debugColumns: false,
  });

  const pageCount = table?.getPageCount();
  const pageIndex = tableParams.currentPage ? tableParams.currentPage - 1 : 0;

  const pageNumbers = Array.from(
    { length: Math.min(pageCount, 5) },
    (_, i) => i + Math.max(0, pageIndex - 2)
  ).filter((page) => page >= 0 && page < pageCount);

  table.getCanPreviousPage = () => tableParams.currentPage !== 1;
  // table.getCanNextPage = () => tableParams.currentPage !== table.getPageCount();
  table.getCanNextPage = () => tableParams.currentPage !== pageCount;

  table.previousPage = () => {
    setTableParams((prev) => ({
      ...prev,
      currentPage: prev.currentPage ? prev.currentPage - 1 : 0,
    }));
  };

  table.nextPage = () => {
    setTableParams((prev) => ({
      ...prev,
      currentPage: prev.currentPage ? prev.currentPage + 1 : 0,
    }));
  };

  const renderColumnFilter = (column: Column<IService, unknown>) => {
    if (column.id === "slug") {
      return getStatusColumnFilter();
    }

    if (column.id === "managedBy") {
      return getManagedByColumnFilter();
    }

    if (column.id === "type") {
      return getTypeColumnFilter();
    }

    if (column.id === "businessServiceType") {
      return getBusinessTypeColumnFilter();
    }

    if (column.id === "createdAt") {
      return getDateColumnFilter("createdAt", createdAtRef);
    }

    if (column.id === "leaseStartDate") {
      return getDateColumnFilter("leaseStartDate", leaseStartDateRef);
    }

    if (column.id === "leaseEndDate") {
      return getDateColumnFilter("leaseEndDate", leaseEndDateRef);
    }

    if (column.id === "appointmentStartDate") {
      return getDateColumnFilter(
        "appointmentStartDate",
        appointmentStartDateRef
      );
    }

    return <Filter column={column} />;
  };

  return (
    <>
      <div className="d-flex flex-column gap-4">
        <h1>Mes commandes</h1>
        {showQuoteBanner && (
          <ErpExpressQuoteBanner
            orderId={erpOrderId}
            message="Un devis a été généré pour le service ERP express que vous venez de créer. Vous pouvez le consulter et le valider."
            linkText="Voir le devis"
          />
        )}
        <div className="d-flex justify-content-between gap-3">
          <div className="search-input-wrapper w-100">
            <i className="search-input-icon pi pi-search" />
            <DebouncedInput
              value={globalFilter ?? ""}
              onChange={handleGlobalFilter}
              className="w-100"
              placeholder="Rechercher les commandes par adresse, bailleur ou nom de l'ancien occupant..."
            />
          </div>
          <Button
            type="button"
            icon="pi pi-filter-slash"
            label="Effacer"
            onClick={clearFilterHandler}
          />
        </div>
        {servicesData?.totalCountServicesStatuses && (
          <div className="d-flex status-filter">
            <div
              className="text-no-wrap me-2"
              onClick={() => onFilterByBadgeSlug(undefined)}
            >
              <ColoredMarker
                active={
                  !tableParams.filters?.find((filter) => filter.id === "slug")
                    ?.value
                }
                className="text-no-wrap"
                color={"#4883F7"}
                bubbleClass={"status-waiting"}
              >
                Tous
              </ColoredMarker>
            </div>
            {Object.entries(servicesData.totalCountServicesStatuses).map(
              ([slug, count], key) => {
                const slugName = slug
                  ? getAppointmentStatusValues(slug).name
                  : undefined;
                return (
                  <div
                    className="text-no-wrap me-2"
                    key={key}
                    onClick={() =>
                      onFilterByBadgeSlug(slug as AppointmentStatusSlug)
                    }
                  >
                    <ServiceStatusComponent
                      slug={slug as AppointmentStatusSlug}
                      count={count}
                      isActive={
                        tableParams.filters?.find(
                          (filter) => filter.id === "slug"
                        )?.value === slugName
                      }
                    />
                  </div>
                );
              }
            )}
            {servicesData?.hasSuperAdminPermission && (
              <div
                className="text-no-wrap ms-auto"
                onClick={() => handleExport()}
              >
                <ColoredMarker className="text-no-wrap" color="#1D6F42">
                  Exporter les commandes <i className="pi pi-file-excel"></i>
                </ColoredMarker>
              </div>
            )}
          </div>
        )}

        <div>
          <div className="custom-table-container">
            {isError ? (
              <div>Erreur: {error.message}</div>
            ) : (
              <table>
                <thead>
                  {table?.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => {
                        return (
                          <th
                            key={header.id}
                            colSpan={header.colSpan}
                            className={
                              Object.entries(
                                header.column.columnDef.meta || {}
                              ).find(
                                ([key, value]) =>
                                  key === "frozen" && value === true
                              )
                                ? "frozen-column"
                                : ""
                            }
                          >
                            {header.isPlaceholder ? null : (
                              <>
                                <TableFilterTitle
                                  title={flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                  )}
                                  sortable={header.column.getCanSort()}
                                  icon={
                                    {
                                      asc: <AscendingIcon className="sorted" />,
                                      desc: (
                                        <DescendingIcon className="sorted" />
                                      ),
                                    }[
                                      header.column.getIsSorted() as string
                                    ] ?? <UnsortedIcon />
                                  }
                                  onClick={header.column.getToggleSortingHandler()}
                                />
                                {header.column.getCanFilter() && (
                                  <div>{renderColumnFilter(header.column)}</div>
                                )}
                              </>
                            )}
                          </th>
                        );
                      })}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {(isLoading || isFetching) && table?.getRowCount() === 0 && (
                    <tr>
                      <td
                        colSpan={columns.length}
                        className="empty-loading-table"
                      />
                    </tr>
                  )}
                  {!isFetching && table?.getRowCount() === 0 ? (
                    <tr>
                      <td colSpan={columns.length}>
                        <div className="mt-4 mb-3">
                          Aucune donnée disponible
                        </div>
                      </td>
                    </tr>
                  ) : (
                    table?.getRowModel().rows.map((row) => (
                      <tr key={row.id}>
                        {row.getVisibleCells().map((cell) => (
                          <td
                            key={cell.id}
                            className={`mx-2 ${
                              Object.entries(
                                cell.column.columnDef.meta || {}
                              ).find(
                                ([key, value]) =>
                                  key === "frozen" && value === true
                              )
                                ? "frozen-column"
                                : ""
                            }`}
                          >
                            <div className="cell-column-container d-flex flex-column align-items-center text-center my-1">
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </div>
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
            {(isLoading || isFetching) && (
              <div className="table-loader-wrapper">
                <Loader />
              </div>
            )}
          </div>
          {!isLoading && !isError && table?.getRowCount() !== 0 && (
            <div className="d-flex justify-content-center custom-pagination-container pt-4 pb-3">
              <div>
                <div className="d-flex gap-1">
                  <div className="d-flex">
                    <button
                      className="pagination-button"
                      onClick={() => {
                        setTableParams((prev) => ({
                          ...prev,
                          currentPage: 1,
                        }));
                      }}
                      disabled={!table?.getCanPreviousPage()}
                    >
                      <DoubleLeftArrowIcon />
                    </button>
                    <button
                      className="pagination-button"
                      onClick={() => table?.previousPage()}
                      disabled={pageIndex <= 0}
                    >
                      <LeftArrowIcon />
                    </button>
                    {pageNumbers.map((page) => (
                      <button
                        className={`pagination-button ${tableParams.currentPage === page + 1 ? "current-page-button" : ""}`}
                        key={page}
                        onClick={() =>
                          setTableParams((prev) => ({
                            ...prev,
                            currentPage: page + 1,
                          }))
                        }
                        disabled={pageIndex === page}
                      >
                        {page + 1}
                      </button>
                    ))}
                    <button
                      className="pagination-button"
                      onClick={() => table?.nextPage()}
                      disabled={pageIndex >= pageCount - 1}
                    >
                      <LeftArrowIcon className="rotate-arrow" />
                    </button>
                    <button
                      className="pagination-button"
                      onClick={() => {
                        setTableParams((prev) => ({
                          ...prev,
                          currentPage: table.getPageCount(),
                        }));
                      }}
                      disabled={!table?.getCanNextPage()}
                    >
                      <DoubleLeftArrowIcon className="rotate-arrow" />
                    </button>
                  </div>
                  {!globalFilter && !columnFilters?.length && (
                    <Dropdown
                      value={tableParams.perPage}
                      options={[10, 20, 30]}
                      onChange={(e) => {
                        const totalPages = Math.ceil(
                          (servicesData?.total ?? 0) / Number(e.target.value)
                        );
                        const newCurrentPage = Math.min(
                          tableParams.currentPage ?? 1,
                          totalPages
                        );
                        setTableParams((prev) => ({
                          ...prev,
                          currentPage: newCurrentPage,
                          perPage: Number(e.target.value),
                        }));
                      }}
                      className="p-column-filter pagination-select"
                    />
                  )}
                </div>
                <div className="mt-2 d-flex justify-content-center gap-1 pagination-text-indicator">
                  Page
                  <strong>
                    {/* {tableParams.currentPage} sur {""} {table?.getPageCount()} */}
                    {tableParams.currentPage} sur {""} {pageCount}
                  </strong>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* === MODAL FOR CUSTOMER INVOICE === */}
      <ModalInfo
        text={`${
          showSuccessInvoiceModal
            ? "La facture pour cette commande a bien été générée"
            : showErrorInvoiceModal
              ? errorInvoiceMessage
              : ""
        }
          `}
        icon={
          <i
            className={`info-icon bi ${
              showSuccessInvoiceModal
                ? "bi-check-circle-fill text-success text-success"
                : showErrorInvoiceModal
                  ? "bi-exclamation-circle-fill text-danger"
                  : ""
            }`}
            style={{ fontSize: showSuccessInvoiceModal ? 50 : 45 }}
          />
        }
        show={showSuccessInvoiceModal || showErrorInvoiceModal}
        onHide={() => {
          showSuccessInvoiceModal
            ? setShowSuccessInvoiceModal(false)
            : setShowErrorInvoiceModal(false);
        }}
      />

      {isLoadingGenerateInvoice && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 z-1 d-flex justify-content-center align-items-center"
          style={{ backgroundColor: "rgba(255, 255, 255, 0.5)" }}
        >
          <Loader />
        </div>
      )}
      {/* === END OF MODAL FOR CUSTOMER INVOICE === */}
    </>
  );
};

function Filter({ column }: { column: Column<any, unknown> }) {
  const columnFilterValue = column.getFilterValue();

  return (
    <DebouncedInput
      type="text"
      value={(columnFilterValue ?? "") as string}
      onChange={(value) => column.setFilterValue(value)}
      className="column-input-search"
    />
  );
}

// A typical debounced input react component
function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 500,
  className = "",
  ...props
}: {
  value: string | number;
  onChange: (value: string | number) => void;
  debounce?: number;
  className?: string;
} & Omit<InputHTMLAttributes<HTMLInputElement>, "onChange">) {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [value, debounce]);

  return (
    <input
      {...props}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      className={`p-inputtext p-component ${className}`}
    />
  );
}

export default MyOrdersContainer;
